import React, { useCallback, useReducer } from 'react';
import {
	createModifierGroupsInitialState,
	CreateModifierGroupsContext,
	TVisibleForm,
	TTypeOfModalWindow,
} from './createModifierGroupsContext';
import { createModifierGroupsReducer } from './createModifierGroupsReducer';
import { CreateModifierGroupsActionTypes } from './createModifierGroupsActions';
import {
	ICreateModifierGroupValidationError,
	IItemForGetShortInfo,
	IServerError,
	TLanguageIDs,
	IDefaultLanguage,
	ICreateModifierGroups,
	IDishesByRestaurantIdsAndSubcategoryIdsItem,
} from 'types';

const CreateModifierGroupsState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(createModifierGroupsReducer, createModifierGroupsInitialState);

	const changeVisibleForm = useCallback((value: TVisibleForm) => {
		dispatch({
			type: CreateModifierGroupsActionTypes.CHANGE_VISIBLE_FORM,
			payload: {
				visibleForm: value,
			},
		});
	}, []);

	const setFetchedCustomers = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateModifierGroupsActionTypes.SET_FETCHED_CUSTOMERS,
			payload: {
				customers: value,
			},
		});
	}, []);

	const setIDCustomer = useCallback((value: Nullable<string>) => {
		dispatch({
			type: CreateModifierGroupsActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	}, []);

	const setFetchedRestaurants = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateModifierGroupsActionTypes.SET_FETCHED_RESTAURANTS,
			payload: {
				restaurants: value,
			},
		});
	}, []);

	const setIDsRestaurant = useCallback((value: string[]) => {
		dispatch({
			type: CreateModifierGroupsActionTypes.SET_IDS_RESTAURANT,
			payload: {
				IDsSelectedRestaurant: value,
			},
		});
	}, []);

	const setFetchedCategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateModifierGroupsActionTypes.SET_FETCHED_CATEGORIES,
			payload: {
				fetchedCategories: value,
			},
		});
	}, []);

	const setIDsCategories = useCallback((value: string[]) => {
		dispatch({
			type: CreateModifierGroupsActionTypes.SET_IDS_CATEGORIES,
			payload: {
				IDsSelectedCategories: value,
			},
		});
	}, []);

	const updateCategories = useCallback(
		(value: IItemForGetShortInfo[]) => {
			const IDsOfCategoriesShortInfo = value.map((el) => el.id);
			const updatedSelectedCategories = state.IDsSelectedCategories.filter((el) =>
				IDsOfCategoriesShortInfo.includes(el),
			);
			dispatch({
				type: CreateModifierGroupsActionTypes.SET_IDS_CATEGORIES,
				payload: {
					IDsSelectedCategories: updatedSelectedCategories,
				},
			});
		},
		[state.IDsSelectedCategories],
	);

	const setFetchedSubcategories = useCallback((value: IItemForGetShortInfo[]) => {
		dispatch({
			type: CreateModifierGroupsActionTypes.SET_FETCHED_SUBCATEGORIES,
			payload: {
				fetchedSubcategories: value,
			},
		});
	}, []);

	const setIDsSubcategories = useCallback((value: string[]) => {
		dispatch({
			type: CreateModifierGroupsActionTypes.SET_IDS_SUBCATEGORIES,
			payload: {
				IDsSelectedSubcategories: value,
			},
		});
	}, []);

	const updateSubcategories = useCallback(
		(value: IItemForGetShortInfo[]) => {
			const IDsOfSubcategoriesShortInfo = value.map((el) => el.id);
			const updatedSelectedSubcategories = state.IDsSelectedSubcategories.filter((el) =>
				IDsOfSubcategoriesShortInfo.includes(el),
			);
			dispatch({
				type: CreateModifierGroupsActionTypes.SET_IDS_SUBCATEGORIES,
				payload: {
					IDsSelectedSubcategories: updatedSelectedSubcategories,
				},
			});
		},
		[state.IDsSelectedSubcategories],
	);

	const setFetchedDishes = useCallback((value: IDishesByRestaurantIdsAndSubcategoryIdsItem[]) => {
		dispatch({
			type: CreateModifierGroupsActionTypes.SET_FETCHED_DISHES,
			payload: {
				dishes: value,
			},
		});
	}, []);

	const setIDsDishes = useCallback((value: string[]) => {
		dispatch({
			type: CreateModifierGroupsActionTypes.SET_IDS_DISHES,
			payload: {
				IDsSelectedDishes: value,
			},
		});
	}, []);

	const updateDishes = useCallback(
		(value: IDishesByRestaurantIdsAndSubcategoryIdsItem[]) => {
			const IDsOfDishesShortInfo = value.map((el) => el.id);
			const updatedSelectedDishes = state.IDsSelectedDishes.filter((el) => IDsOfDishesShortInfo.includes(el));
			dispatch({
				type: CreateModifierGroupsActionTypes.SET_IDS_DISHES,
				payload: {
					IDsSelectedDishes: updatedSelectedDishes,
				},
			});
		},
		[state.IDsSelectedDishes],
	);

	const changeNameOfModifierGroup = useCallback((value: string) => {
		dispatch({
			type: CreateModifierGroupsActionTypes.CHANGE_NAME_OF_MODIFIER_GROUP,
			payload: {
				nameOfModifierGroup: value,
			},
		});
	}, []);

	const setFetchedTranslations = useCallback(
		(value: TLanguageIDs[], defaultCustomerLanguage: Nullable<IDefaultLanguage>) => {
			const translations = value.map((id) => ({ languageId: id, name: '' }));
			dispatch({
				type: CreateModifierGroupsActionTypes.SET_FETCHED_TRANSLATIONS,
				payload: {
					translations,
					defaultCustomerLanguage,
				},
			});
		},
		[],
	);

	const setTranslationTab = useCallback((value: TLanguageIDs | null) => {
		dispatch({
			type: CreateModifierGroupsActionTypes.SET_TRANSLATION_TAB,
			payload: {
				selectedTranslationTab: value,
			},
		});
	}, []);

	const changeNameTranslation = useCallback(
		(languageId: TLanguageIDs, value: string) => {
			const updatedTranslations = state.translations.map((translation) =>
				translation.languageId === languageId
					? {
							...translation,
							name: value,
					  }
					: translation,
			);
			dispatch({
				type: CreateModifierGroupsActionTypes.CHANGE_TRANSLATION,
				payload: {
					translations: updatedTranslations,
				},
			});
		},
		[state.translations],
	);

	const setDataForSubmit = useCallback((value: ICreateModifierGroups | null) => {
		dispatch({
			type: CreateModifierGroupsActionTypes.SET_DATA_FOR_SUBMIT,
			payload: {
				dataForSubmit: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: CreateModifierGroupsActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const showErrorModalWithInfo = useCallback((value: IServerError<ICreateModifierGroupValidationError>) => {
		dispatch({
			type: CreateModifierGroupsActionTypes.SHOW_ERROR_MODAL_WITH_INFO,
			payload: {
				errorResponseData: value,
			},
		});
	}, []);

	const resetFields = useCallback(() => dispatch({ type: CreateModifierGroupsActionTypes.RESET_FIELDS }), []);

	return (
		<CreateModifierGroupsContext.Provider
			value={{
				changeVisibleForm,
				setFetchedCustomers,
				setIDCustomer,
				setFetchedRestaurants,
				setIDsRestaurant,
				setFetchedCategories,
				setIDsCategories,
				updateCategories,
				setFetchedSubcategories,
				setIDsSubcategories,
				updateSubcategories,
				setFetchedDishes,
				setIDsDishes,
				updateDishes,
				changeNameOfModifierGroup,
				setFetchedTranslations,
				setTranslationTab,
				changeNameTranslation,
				setDataForSubmit,
				changeTypeOfModalWindow,
				showErrorModalWithInfo,
				resetFields,
				visibleForm: state.visibleForm,
				fetchedCustomers: state.fetchedCustomers,
				IDSelectedCustomer: state.IDSelectedCustomer,
				fetchedRestaurants: state.fetchedRestaurants,
				IDsSelectedRestaurant: state.IDsSelectedRestaurant,
				fetchedCategories: state.fetchedCategories,
				IDsSelectedCategories: state.IDsSelectedCategories,
				fetchedSubcategories: state.fetchedSubcategories,
				IDsSelectedSubcategories: state.IDsSelectedSubcategories,
				fetchedDishes: state.fetchedDishes,
				IDsSelectedDishes: state.IDsSelectedDishes,
				nameOfModifierGroup: state.nameOfModifierGroup,
				defaultCustomerLanguage: state.defaultCustomerLanguage,
				selectedTranslationTab: state.selectedTranslationTab,
				translations: state.translations,
				dataForSubmit: state.dataForSubmit,
				typeOfModalWindow: state.typeOfModalWindow,
				errorResponseData: state.errorResponseData,
			}}>
			{children}
		</CreateModifierGroupsContext.Provider>
	);
};

export default CreateModifierGroupsState;
